import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getAnalyses, deleteAnalysis } from '../utils/api';
import { useAuth } from '../utils/AuthContext';

const Dashboard = () => {
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all'); // all, low, moderate, high, critical
  const [sortBy, setSortBy] = useState('date'); // date, risk, name
  const { user } = useAuth();

  useEffect(() => {
    loadAnalyses();
  }, []);

  const loadAnalyses = async () => {
    try {
      setLoading(true);
      const response = await getAnalyses();
      setAnalyses(response.data.analyses || []);
    } catch (err) {
      setError('Failed to load analysis history');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (analysisId, contractName) => {
    if (!window.confirm(`Are you sure you want to delete "${contractName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await deleteAnalysis(analysisId);
      setAnalyses(analyses.filter(a => a.id !== analysisId));
    } catch (err) {
      setError('Failed to delete analysis');
      console.error(err);
    }
  };

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-300';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'moderate': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'low': return 'bg-green-100 text-green-800 border-green-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getRiskScoreColor = (score) => {
    if (score >= 86) return 'text-red-600';
    if (score >= 61) return 'text-orange-600';
    if (score >= 31) return 'text-yellow-600';
    return 'text-green-600';
  };

  const filteredAnalyses = analyses
    .filter(analysis => filter === 'all' || analysis.overall_risk === filter)
    .sort((a, b) => {
      if (sortBy === 'date') {
        return new Date(b.created_at) - new Date(a.created_at);
      } else if (sortBy === 'risk') {
        return (b.risk_score || 0) - (a.risk_score || 0);
      } else {
        return a.contract_name.localeCompare(b.contract_name);
      }
    });

  const contractTypeIcons = {
    property: '🏠',
    lease: '📋',
    employment: '💼',
    freelance: '✍️',
    vendor: '🤝',
    nda: '🔒',
    service: '⚙️',
    other: '📄'
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          <span className="ml-4 text-lg">Loading your analyses...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Contract Analysis Dashboard</h1>
        <p className="text-gray-600">View and manage all your contract analyses</p>
        {user && (
          <div className="mt-4 flex items-center gap-4">
            <div className="bg-primary-50 border border-primary-200 rounded-lg px-4 py-2">
              <span className="text-sm text-gray-600">Scans Remaining:</span>
              <span className="ml-2 text-lg font-bold text-primary-600">{user.scans_remaining}</span>
            </div>
            <Link
              to="/upload"
              className="bg-primary-600 text-white hover:bg-primary-700 px-6 py-2 rounded-lg font-semibold"
            >
              New Analysis
            </Link>
          </div>
        )}
      </div>

      {/* Filters and Sorting */}
      <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex flex-wrap gap-2">
            <span className="text-sm font-medium text-gray-700 self-center mr-2">Filter by Risk:</span>
            {['all', 'low', 'moderate', 'high', 'critical'].map((riskLevel) => (
              <button
                key={riskLevel}
                onClick={() => setFilter(riskLevel)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                  filter === riskLevel
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {riskLevel.charAt(0).toUpperCase() + riskLevel.slice(1)}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">Sort by:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="date">Date (Newest First)</option>
              <option value="risk">Risk Score</option>
              <option value="name">Contract Name</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {/* Analyses List */}
      {filteredAnalyses.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
          <div className="text-6xl mb-4">📄</div>
          <h3 className="text-xl font-semibold mb-2">No Analyses Found</h3>
          <p className="text-gray-600 mb-6">
            {analyses.length === 0
              ? "You haven't analysed any contracts yet."
              : "No analyses match the selected filter."}
          </p>
          {analyses.length === 0 && (
            <Link
              to="/upload"
              className="inline-block bg-primary-600 text-white hover:bg-primary-700 px-6 py-3 rounded-lg font-semibold"
            >
              Analyse Your First Contract
            </Link>
          )}
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredAnalyses.map((analysis) => (
            <div key={analysis.id} className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow">
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-2xl">
                        {contractTypeIcons[analysis.contract_type] || '📄'}
                      </span>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {analysis.contract_name}
                      </h3>
                    </div>
                    <div className="flex flex-wrap gap-2 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        {new Date(analysis.created_at).toLocaleDateString()}
                      </span>
                      <span>•</span>
                      <span className="capitalize">{analysis.contract_type.replace('_', ' ')}</span>
                      <span>•</span>
                      <span>{analysis.page_count} pages</span>
                      {analysis.contract_country && (
                        <>
                          <span>•</span>
                          <span>{analysis.contract_country}</span>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col items-end gap-2">
                    <div className={`px-3 py-1 rounded-full text-xs font-semibold border ${getRiskColor(analysis.overall_risk)}`}>
                      {analysis.overall_risk?.toUpperCase() || 'UNKNOWN'} RISK
                    </div>
                    <div className={`text-3xl font-bold ${getRiskScoreColor(analysis.risk_score)}`}>
                      {analysis.risk_score || 0}
                      <span className="text-sm text-gray-500">/100</span>
                    </div>
                  </div>
                </div>

                {/* Quick Stats */}
                {analysis.analysis_result && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                    {analysis.analysis_result.risk_scores && (
                      <>
                        <div className="text-center p-2 bg-gray-50 rounded">
                          <div className="text-xs text-gray-600 mb-1">Legal Risk</div>
                          <div className={`text-lg font-semibold ${getRiskScoreColor(analysis.analysis_result.risk_scores.legal_risk)}`}>
                            {analysis.analysis_result.risk_scores.legal_risk || 0}
                          </div>
                        </div>
                        <div className="text-center p-2 bg-gray-50 rounded">
                          <div className="text-xs text-gray-600 mb-1">Financial Risk</div>
                          <div className={`text-lg font-semibold ${getRiskScoreColor(analysis.analysis_result.risk_scores.financial_risk)}`}>
                            {analysis.analysis_result.risk_scores.financial_risk || 0}
                          </div>
                        </div>
                        <div className="text-center p-2 bg-gray-50 rounded">
                          <div className="text-xs text-gray-600 mb-1">Fairness</div>
                          <div className={`text-lg font-semibold ${getRiskScoreColor(100 - (analysis.analysis_result.risk_scores.fairness_score || 0))}`}>
                            {analysis.analysis_result.risk_scores.fairness_score || 0}
                          </div>
                        </div>
                        <div className="text-center p-2 bg-gray-50 rounded">
                          <div className="text-xs text-gray-600 mb-1">Clarity</div>
                          <div className={`text-lg font-semibold ${getRiskScoreColor(100 - (analysis.analysis_result.risk_scores.clarity_score || 0))}`}>
                            {analysis.analysis_result.risk_scores.clarity_score || 0}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Key Findings Preview */}
                {analysis.analysis_result?.key_findings?.length > 0 && (
                  <div className="mb-4">
                    <div className="text-sm font-medium text-gray-700 mb-2">Key Findings:</div>
                    <div className="flex flex-wrap gap-2">
                      {analysis.analysis_result.key_findings.slice(0, 3).map((finding, idx) => (
                        <span
                          key={idx}
                          className={`text-xs px-2 py-1 rounded ${
                            finding.severity === 'critical' ? 'bg-red-100 text-red-700' :
                            finding.severity === 'high' ? 'bg-orange-100 text-orange-700' :
                            finding.severity === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-blue-100 text-blue-700'
                          }`}
                        >
                          {finding.category || finding.description?.substring(0, 30) || 'Issue found'}
                        </span>
                      ))}
                      {analysis.analysis_result.key_findings.length > 3 && (
                        <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-600">
                          +{analysis.analysis_result.key_findings.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2">
                  <Link
                    to={`/results/${analysis.id}`}
                    className="flex-1 sm:flex-none bg-primary-600 text-white hover:bg-primary-700 px-4 py-2 rounded-lg font-medium text-center transition-colors"
                  >
                    View Full Report
                  </Link>
                  <button
                    onClick={() => {/* TODO: Implement download */}}
                    className="border border-gray-300 hover:bg-gray-50 px-4 py-2 rounded-lg font-medium transition-colors"
                    title="Download PDF Report"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleDelete(analysis.id, analysis.contract_name)}
                    className="border border-red-300 hover:bg-red-50 text-red-600 px-4 py-2 rounded-lg font-medium transition-colors"
                    title="Delete Analysis"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Stats Footer */}
      {analyses.length > 0 && (
        <div className="mt-8 bg-gray-50 rounded-lg p-6 border">
          <h3 className="text-lg font-semibold mb-4">Your Statistics</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <div className="text-2xl font-bold text-primary-600">{analyses.length}</div>
              <div className="text-sm text-gray-600">Total Analyses</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-red-600">
                {analyses.filter(a => a.overall_risk === 'critical' || a.overall_risk === 'high').length}
              </div>
              <div className="text-sm text-gray-600">High Risk Contracts</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-green-600">
                {analyses.filter(a => a.overall_risk === 'low').length}
              </div>
              <div className="text-sm text-gray-600">Low Risk Contracts</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">
                {Math.round(analyses.reduce((sum, a) => sum + (a.risk_score || 0), 0) / analyses.length) || 0}
              </div>
              <div className="text-sm text-gray-600">Average Risk Score</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
